import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "../components/ui/button";
import {
  BadgeCheck,
  CalendarCheck2,
  Wallet,
  MessageSquare,
  Sparkles,
  Rocket,
  Users,
  ChevronDown,
  ShieldCheck,
  CheckCircle2,
  BarChart3,
  Star,
} from "lucide-react";

// Reusable motion helpers
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: "easeOut", delay },
});

export default function JoinUs() {
  const prefersReducedMotion = useReducedMotion();

  // FAQ data + local accordion state (multi-open with Set)
  const faqs = React.useMemo(
    () => [
      {
        icon: <Wallet className="h-5 w-5" />,
        q: "Is there any upfront cost to join?",
        a: "No. Your public coach listing is free. Lead Access costs AED 199 monthly or AED 1,910.00 yearly (save 20%) when you choose to unlock client inquiries.",
      },
      {
        icon: <MessageSquare className="h-5 w-5" />,
        q: "How do clients contact me?",
        a: "Clients send inquiries and booking requests through PTfinderNow. Subscribe to Lead Access to view their details and reply.",
      },
      {
        icon: <BarChart3 className="h-5 w-5" />,
        q: "Do I control my pricing?",
        a: "100%. Set a price per session—or show “Request Price” if you prefer. We never change your rates.",
      },
      {
        icon: <CalendarCheck2 className="h-5 w-5" />,
        q: "How are bookings confirmed?",
        a: "You tap Accept or Cancel in your dashboard. When accepted, the time slot is automatically blocked to prevent double-booking.",
      },
      {
        icon: <ShieldCheck className="h-5 w-5" />,
        q: "How does trust work during pre-launch?",
        a: "During pre-launch, trust is built through real client reviews after confirmed sessions. Platform verification (manual review / ID checks) will be introduced later.",
      },
      {
        icon: <Users className="h-5 w-5" />,
        q: "How do reviews work?",
        a: "After confirmed sessions, clients can leave reviews. Great service = great reviews = more visibility and bookings over time.",
      },
      {
        icon: <CheckCircle2 className="h-5 w-5" />,
        q: "Can I pause or leave anytime?",
        a: "Yes. Pause your availability, hide your profile, or cancel a plan anytime—no lock-in.",
      },
    ],
    []
  );

  const [openFaq, setOpenFaq] = React.useState(new Set());
  const toggleFaq = (i) =>
    setOpenFaq((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ========== HERO ========== */}
      <section className="relative h-[82vh] md:h-[92vh] w-full overflow-hidden">
        {/* Background video with graceful fallback (no image used) */}
        <VideoBackdrop reduced={prefersReducedMotion} />

        {/* Vignette + gradient tint */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/55" />
        <div className="pointer-events-none absolute inset-0 [box-shadow:inset_0_0_80px_rgba(0,0,0,.45)]" />

        {/* Content */}
        <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col items-center justify-center px-6 text-center text-white">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm backdrop-blur"
          >
            <Sparkles className="h-4 w-4" />
            Pre-launch onboarding is open
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-4 text-4xl font-extrabold tracking-tight md:text-6xl"
          >
            <span className="bg-gradient-to-r from-indigo-300 via-white to-purple-200 bg-clip-text text-transparent">
              Join PTfinderNow
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mx-auto mt-4 max-w-2xl text-lg md:text-2xl text-white/90"
          >
            Connect with clients. Own your schedule. Build trust with real reviews over time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <Link to="/signup">
              <Button className="text-base md:text-lg h-12 rounded-full px-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-95">
                Join Now
              </Button>
            </Link>
            <a href="#how-it-works" className="group inline-flex items-center">
              <Button
                variant="secondary"
                className="text-base md:text-lg h-12 rounded-full px-6 bg-white/10 text-white hover:bg-white/15 border-white/20"
              >
                How it works
              </Button>
            </a>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            {...fadeUp(0.6)}
            className="mt-10 grid grid-cols-2 gap-3 text-sm text-white/80 sm:grid-cols-4"
          >
            <TrustPill icon={<Star className="h-4 w-4" />} label="Client reviews" />
            <TrustPill icon={<Users className="h-4 w-4" />} label="High-intent clients" />
            <TrustPill icon={<CalendarCheck2 className="h-4 w-4" />} label="Effortless bookings" />
            <TrustPill icon={<Wallet className="h-4 w-4" />} label="No upfront cost" />
          </motion.div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <motion.h2
          {...fadeUp()}
          className="text-center text-3xl font-extrabold tracking-tight text-gray-900 md:text-4xl"
        >
          Built for experts who want to win
        </motion.h2>

        <motion.p
          {...fadeUp(0.1)}
          className="mx-auto mt-3 max-w-2xl text-center text-gray-600 md:text-lg"
        >
          A clean profile, better leads, and a dashboard that helps you stay focused on your clients.
        </motion.p>

        <div className="mt-10 grid grid-cols-1 gap-6 md:mt-14 md:grid-cols-2">
          <FeatureCard
            icon={<Users className="h-5 w-5" />}
            title="High-intent Clients"
            description="Get discovered by people actively looking for your service—less noise, more serious requests."
          />
          <FeatureCard
            icon={<CalendarCheck2 className="h-5 w-5" />}
            title="Total Flexibility"
            description="Set availability, session types, locations—everything under your control."
          />
          <FeatureCard
            icon={<Rocket className="h-5 w-5" />}
            title="Effortless Bookings"
            description="Clients request directly from your calendar. You accept or cancel with one tap."
          />
          <FeatureCard
            icon={<MessageSquare className="h-5 w-5" />}
            title="Direct Communication"
            description="Client inquiries in your private inbox, unlocked with Lead Access."
          />
          <FeatureCard
            icon={<Wallet className="h-5 w-5" />}
            title="Zero Upfront Cost"
            description="Free public listing. Unlock client inquiries for AED 199 monthly or AED 1,910.00 yearly."
          />
          <FeatureCard
            icon={<BadgeCheck className="h-5 w-5" />}
            title="Professional Profile"
            description="Showcase specialties, photos, and real client reviews in a clean, high-converting design."
          />
        </div>
      </section>

      {/* ========== DASHBOARD / CTA PANEL ========== */}
      <section className="mx-auto max-w-6xl px-6">
        <motion.div
          {...fadeUp()}
          className="rounded-2xl bg-white p-8 shadow-2xl ring-1 ring-gray-100"
        >
          <h3 className="text-center text-3xl font-bold text-gray-900 md:text-4xl">
            Your Personal Dashboard
          </h3>
          <p className="mx-auto mt-3 max-w-3xl text-center text-gray-600 md:text-lg">
            Manage availability, bookings, leads, and your profile—from one sleek hub designed for speed and clarity.
          </p>

          <div className="mt-6 flex justify-center">
            <Link to="/signup">
              <Button className="h-12 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-8 text-base md:text-lg text-white hover:opacity-95">
                Apply Now
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section id="how-it-works" className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <motion.h4
          {...fadeUp()}
          className="text-center text-2xl font-extrabold tracking-tight text-gray-900 md:text-3xl"
        >
          How it works
        </motion.h4>

        <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
          <StepCard
            step="01"
            title="Create your profile"
            text="Add your specialties, locations, pricing (or show ‘Request Price’), and gallery."
          />
          <StepCard
            step="02"
            title="Set availability"
            text="Publish time slots so clients can request sessions instantly."
          />
          <StepCard
            step="03"
            title="Get requests"
            text="Requests appear in your dashboard. Lead Access unlocks their details and replies."
          />
        </div>
      </section>

      {/* ========== TRUST NOTE (honest, pre-launch) ========== */}
      <section className="mx-auto max-w-6xl px-6 pb-4">
        <motion.div
          {...fadeUp()}
          className="rounded-2xl bg-white p-6 shadow-md ring-1 ring-gray-100"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Trust & Reviews</h3>
              <p className="mt-1 text-gray-600">
                In pre-launch, we don’t manually verify every expert. Trust is built through real client reviews after confirmed sessions.
                Platform verification will be added later.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Reviews after sessions
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Transparent profiles
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Verification later
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ========== FAQ (inline) ========== */}
      <section className="mx-auto max-w-5xl px-6 pb-16">
        <motion.h4
          {...fadeUp()}
          className="text-center text-2xl font-extrabold tracking-tight text-gray-900 md:text-3xl"
        >
          FAQ — For Experts
        </motion.h4>
        <p className="mt-3 text-center text-gray-600 md:text-lg">
          Straight answers so you can focus on what you do best.
        </p>

        <div className="mt-8 space-y-3">
          {faqs.map((item, i) => {
            const isOpen = openFaq.has(i);
            return (
              <div
                key={i}
                className="group rounded-2xl bg-white p-4 md:p-5 shadow-md ring-1 ring-gray-100 transition hover:shadow-lg"
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  onClick={() => toggleFaq(i)}
                  className="flex w-full items-center gap-3 text-left"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                    {item.icon}
                  </div>
                  <span className="flex-1 text-base md:text-lg font-semibold text-gray-900">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <motion.div
                  id={`faq-panel-${i}`}
                  role="region"
                  initial={false}
                  animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="pl-[3.75rem] pr-1 pt-3 text-gray-600">{item.a}</div>
                </motion.div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <Link to="/signup">
            <Button className="h-12 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-8 text-base md:text-lg text-white hover:opacity-95">
              Ready to grow? Join now
            </Button>
          </Link>
        </div>
      </section>

      {/* ========== FOOTER TAGLINE ========== */}
      <section className="px-6 pb-16">
        <motion.div
          {...fadeUp()}
          className="mx-auto max-w-3xl rounded-2xl bg-white/60 p-6 text-center text-gray-700 shadow-sm ring-1 ring-gray-100 backdrop-blur"
        >
          ✨ Excellence Meets Opportunity at <span className="font-semibold">PTfinderNow</span> ✨
        </motion.div>
      </section>
    </div>
  );
}

/* ======================= subcomponents ======================= */

function VideoBackdrop({ reduced }) {
  // No image fallback — only video or a premium gradient.
  return (
    <div className="absolute inset-0">
      {!reduced ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute h-full w-full object-cover"
          src="/Videos/ptfinder-joinus.mp4" // ensure this file exists in /public/Videos/
          aria-label="Experts working with clients, inspiring moments"
        />
      ) : (
        <div
          className="absolute h-full w-full bg-gradient-to-br from-indigo-900 via-purple-900 to-black"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function TrustPill({ icon, label }) {
  return (
    <div className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs backdrop-blur">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <motion.div
      {...fadeUp(0.05)}
      className="group relative rounded-2xl bg-white p-6 shadow-md ring-1 ring-gray-100 transition hover:shadow-xl"
    >
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-indigo-500/0 via-purple-500/0 to-transparent opacity-0 blur-xl transition group-hover:opacity-20" />
      <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-gray-600">{description}</p>
    </motion.div>
  );
}

function StepCard({ step, title, text }) {
  return (
    <motion.div
      {...fadeUp(0.05)}
      className="relative rounded-2xl bg-white p-6 shadow-md ring-1 ring-gray-100"
    >
      <div className="absolute right-6 top-6 text-5xl font-black leading-none text-gray-100">
        {step}
      </div>
      <h5 className="text-lg font-semibold text-gray-900">{title}</h5>
      <p className="mt-2 text-gray-600">{text}</p>
    </motion.div>
  );
}
